import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Table,
  Select,
  Spin,
  Empty,
  Tag,
  Tabs,
} from "antd";
import {
  EyeOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  TrophyOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { apiService } from "@/services/api";

const { Title, Text } = Typography;

export const StatisticsPage: React.FC = () => {
  const [period, setPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState("overview");

  // Overview tab state
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [mostWatched, setMostWatched] = useState<any[]>([]);
  const [error, setError] = useState(false);

  // Content Performance tab state
  const [contentList, setContentList] = useState<any[]>([]);
  const [contentListLoading, setContentListLoading] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(null);
  const [contentDetail, setContentDetail] = useState<any>(null);
  const [contentDetailLoading, setContentDetailLoading] = useState(false);

  // Revenue tab state
  const [revenueStats, setRevenueStats] = useState<any>(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Bandwidth tab state
  const [bandwidthData, setBandwidthData] = useState<any[]>([]);
  const [bandwidthLoading, setBandwidthLoading] = useState(false);

  // === Overview data fetch ===
  const fetchOverviewData = useCallback(async (days: number) => {
    setLoading(true);
    setError(false);
    try {
      const [dashboard, watched] = await Promise.allSettled([
        apiService.getAnalyticsDashboard(days),
        apiService.getMostWatched(days, 10),
      ]);
      if (dashboard.status === "fulfilled") setDashboardData(dashboard.value);
      if (watched.status === "fulfilled")
        setMostWatched(Array.isArray(watched.value) ? watched.value : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // === Content Performance data ===
  const loadContentList = useCallback(async () => {
    setContentListLoading(true);
    try {
      const res = await apiService.getContents(undefined, 1, 100);
      setContentList(res.data || []);
    } catch {
      setContentList([]);
    } finally {
      setContentListLoading(false);
    }
  }, []);

  const loadContentDetail = useCallback(async (contentId: number, days: number) => {
    setContentDetailLoading(true);
    try {
      const data = await apiService.getContentStatistics(contentId, days);
      setContentDetail(data);
    } catch {
      setContentDetail(null);
    } finally {
      setContentDetailLoading(false);
    }
  }, []);

  // === Revenue data ===
  const loadRevenueStats = useCallback(async () => {
    setRevenueLoading(true);
    try {
      const data = await apiService.getPaymentStatistics();
      setRevenueStats(data);
    } catch {
      setRevenueStats(null);
    } finally {
      setRevenueLoading(false);
    }
  }, []);

  // === Bandwidth data ===
  const loadBandwidthData = useCallback(async (days: number) => {
    setBandwidthLoading(true);
    try {
      const data = await apiService.getBandwidthByLocation(days);
      setBandwidthData(Array.isArray(data) ? data : []);
    } catch {
      setBandwidthData([]);
    } finally {
      setBandwidthLoading(false);
    }
  }, []);

  // Initial load + period change
  useEffect(() => {
    fetchOverviewData(period);
  }, [period, fetchOverviewData]);

  // Lazy-load tab data
  useEffect(() => {
    if (activeTab === "content-performance" && contentList.length === 0) {
      loadContentList();
    }
    if (activeTab === "revenue" && !revenueStats) {
      loadRevenueStats();
    }
    if (activeTab === "bandwidth") {
      loadBandwidthData(period);
    }
  }, [activeTab]);

  // Re-fetch content detail when period changes
  useEffect(() => {
    if (activeTab === "content-performance" && selectedContentId) {
      loadContentDetail(selectedContentId, period);
    }
    if (activeTab === "bandwidth") {
      loadBandwidthData(period);
    }
  }, [period]);

  const formatNumber = (num: number) => {
    if (!num) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const getPaymentStatusColor = (status: string) => {
    const map: Record<string, string> = {
      SUCCESS: "green", COMPLETED: "green",
      FAILED: "red",
      PENDING: "orange",
      CANCELLED: "default",
    };
    return map[status] || "default";
  };

  // === Columns ===
  const mostWatchedColumns = [
    {
      title: "#",
      key: "rank",
      width: 50,
      render: (_: any, __: any, index: number) => (
        <Tag color={index < 3 ? "gold" : "default"}>{index + 1}</Tag>
      ),
    },
    {
      title: "Contenu",
      dataIndex: "contentTitle",
      key: "contentTitle",
      render: (title: string, record: any) => (
        <Text strong>{title || record.title || `Contenu #${record.contentId}`}</Text>
      ),
    },
    {
      title: "Vues",
      dataIndex: "viewCount",
      key: "viewCount",
      render: (count: number) => formatNumber(count || 0),
    },
    {
      title: "Taux de complétion",
      dataIndex: "completionRate",
      key: "completionRate",
      render: (rate: number) => (rate ? `${(rate * 100).toFixed(1)}%` : "--"),
    },
  ];

  // === Tab content renderers ===
  const renderOverviewTab = () => (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Vues"
              value={dashboardData?.totalViews || dashboardData?.totalEvents || 0}
              formatter={(value) => formatNumber(Number(value))}
              prefix={<EyeOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Sessions"
              value={dashboardData?.totalSessions || dashboardData?.uniqueSessions || 0}
              formatter={(value) => formatNumber(Number(value))}
              prefix={<PlayCircleOutlined style={{ color: "#52c41a" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Taux de complétion moyen"
              value={dashboardData?.averageCompletionRate ? (dashboardData.averageCompletionRate * 100).toFixed(1) : 0}
              suffix="%"
              prefix={<ClockCircleOutlined style={{ color: "#722ed1" }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Erreurs"
              value={dashboardData?.totalErrors || 0}
              prefix={<WarningOutlined style={{ color: dashboardData?.totalErrors > 0 ? "#ff4d4f" : "#52c41a" }} />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card
            title={
              <span>
                <TrophyOutlined style={{ color: "#faad14", marginRight: 8 }} />
                Contenus les plus regardés
              </span>
            }
          >
            {mostWatched.length > 0 ? (
              <Table
                dataSource={mostWatched}
                columns={mostWatchedColumns}
                pagination={false}
                size="small"
                rowKey={(record: any) => record.contentId || record.id || Math.random()}
              />
            ) : (
              <Empty
                description={
                  error
                    ? "Impossible de charger les données analytics. Vérifiez que le service est accessible."
                    : "Aucune donnée de visionnage disponible pour cette période."
                }
              />
            )}
          </Card>
        </Col>
      </Row>

      {dashboardData && (
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Distribution par appareil">
              {dashboardData.deviceDistribution && Object.keys(dashboardData.deviceDistribution).length > 0 ? (
                <Table
                  dataSource={Object.entries(dashboardData.deviceDistribution).map(
                    ([device, count]) => ({ device, count })
                  )}
                  columns={[
                    { title: "Appareil", dataIndex: "device", key: "device" },
                    { title: "Sessions", dataIndex: "count", key: "count", render: (val: any) => formatNumber(Number(val)) },
                  ]}
                  pagination={false}
                  size="small"
                  rowKey="device"
                />
              ) : (
                <Empty description="Pas de données disponibles" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Distribution par qualité vidéo">
              {dashboardData.qualityDistribution && Object.keys(dashboardData.qualityDistribution).length > 0 ? (
                <Table
                  dataSource={Object.entries(dashboardData.qualityDistribution).map(
                    ([quality, count]) => ({ quality, count })
                  )}
                  columns={[
                    { title: "Qualité", dataIndex: "quality", key: "quality" },
                    { title: "Vues", dataIndex: "count", key: "count", render: (val: any) => formatNumber(Number(val)) },
                  ]}
                  pagination={false}
                  size="small"
                  rowKey="quality"
                />
              ) : (
                <Empty description="Pas de données disponibles" />
              )}
            </Card>
          </Col>
        </Row>
      )}

      {!loading && !dashboardData && !error && (
        <Card>
          <Empty
            description={
              <div>
                <Text>Aucune donnée analytique disponible</Text>
                <br />
                <Text type="secondary">
                  Les statistiques seront affichées lorsque des utilisateurs commenceront à regarder du contenu.
                </Text>
              </div>
            }
          />
        </Card>
      )}
    </Spin>
  );

  const renderContentPerformanceTab = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Select
          showSearch
          placeholder="Sélectionnez un contenu..."
          style={{ width: 400 }}
          loading={contentListLoading}
          value={selectedContentId}
          onChange={(val) => {
            setSelectedContentId(val);
            if (val) loadContentDetail(val, period);
          }}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={contentList.map((c: any) => ({
            value: c.id,
            label: c.title || `Contenu #${c.id}`,
          }))}
        />
      </div>

      {!selectedContentId ? (
        <Card>
          <Empty
            image={<VideoCameraOutlined style={{ fontSize: 48, color: "#d9d9d9" }} />}
            imageStyle={{ height: 60 }}
            description="Sélectionnez un contenu pour voir ses performances détaillées"
          />
        </Card>
      ) : (
        <Spin spinning={contentDetailLoading}>
          {contentDetail ? (
            <>
              {/* KPIs */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Taux de complétion"
                      value={contentDetail.completionRate ? (contentDetail.completionRate * 100).toFixed(1) : 0}
                      suffix="%"
                      prefix={<ClockCircleOutlined style={{ color: "#722ed1" }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Événements buffering"
                      value={contentDetail.bufferingStats?.bufferCount || 0}
                      prefix={<ThunderboltOutlined style={{ color: "#faad14" }} />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Erreurs"
                      value={contentDetail.errorCount || 0}
                      prefix={<WarningOutlined style={{ color: contentDetail.errorCount > 0 ? "#ff4d4f" : "#52c41a" }} />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Detail tables */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                  <Card title="Distribution par qualité">
                    {contentDetail.qualityDistribution?.length > 0 ? (
                      <Table
                        dataSource={contentDetail.qualityDistribution}
                        columns={[
                          { title: "Qualité", dataIndex: "quality", key: "quality" },
                          { title: "Nombre", dataIndex: "count", key: "count", render: (v: number) => formatNumber(v) },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="quality"
                      />
                    ) : (
                      <Empty description="Pas de données" />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="Distribution par appareil">
                    {contentDetail.deviceDistribution?.length > 0 ? (
                      <Table
                        dataSource={contentDetail.deviceDistribution}
                        columns={[
                          { title: "Appareil", dataIndex: "deviceType", key: "deviceType" },
                          { title: "Nombre", dataIndex: "count", key: "count", render: (v: number) => formatNumber(v) },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="deviceType"
                      />
                    ) : (
                      <Empty description="Pas de données" />
                    )}
                  </Card>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Card title="Statistiques de connexion">
                    {contentDetail.connectionStats?.length > 0 ? (
                      <Table
                        dataSource={contentDetail.connectionStats}
                        columns={[
                          { title: "Type", dataIndex: "connectionType", key: "connectionType" },
                          {
                            title: "Bande passante moy.",
                            dataIndex: "avgBandwidth",
                            key: "avgBandwidth",
                            render: (v: number) => (v ? `${v.toFixed(1)} kbps` : "--"),
                          },
                          {
                            title: "Buffer moy.",
                            dataIndex: "avgBufferDuration",
                            key: "avgBufferDuration",
                            render: (v: number) => (v ? `${v.toFixed(0)} ms` : "--"),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="connectionType"
                      />
                    ) : (
                      <Empty description="Pas de données" />
                    )}
                  </Card>
                </Col>
                <Col xs={24} lg={12}>
                  <Card title="Erreurs récentes">
                    {contentDetail.recentErrors?.length > 0 ? (
                      <Table
                        dataSource={contentDetail.recentErrors}
                        columns={[
                          { title: "Code", dataIndex: "errorCode", key: "errorCode" },
                          { title: "Message", dataIndex: "errorMessage", key: "errorMessage", ellipsis: true },
                          {
                            title: "Date",
                            dataIndex: "eventTimestamp",
                            key: "eventTimestamp",
                            render: (v: string) => (v ? new Date(v).toLocaleString("fr-FR") : "--"),
                          },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey={(r: any) => r.id || r.eventTimestamp || Math.random()}
                      />
                    ) : (
                      <Empty description="Aucune erreur" />
                    )}
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Card>
              <Empty description="Aucune donnée disponible pour ce contenu" />
            </Card>
          )}
        </Spin>
      )}
    </div>
  );

  const renderRevenueTab = () => (
    <Spin spinning={revenueLoading}>
      {revenueStats ? (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Montant total"
                  value={revenueStats.totalAmount || 0}
                  prefix={<DollarOutlined style={{ color: "#52c41a" }} />}
                  suffix="XOF"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total paiements"
                  value={revenueStats.totalPayments || 0}
                  prefix={<CreditCardOutlined style={{ color: "#1890ff" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Taux de réussite"
                  value={revenueStats.successRate || 0}
                  suffix="%"
                  precision={1}
                  prefix={<CheckCircleOutlined style={{ color: "#722ed1" }} />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Taux d'échec"
                  value={revenueStats.failureRate || 0}
                  suffix="%"
                  precision={1}
                  prefix={<WarningOutlined style={{ color: "#ff4d4f" }} />}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={8}>
              <Card title="Par fournisseur">
                {revenueStats.byProvider && Object.keys(revenueStats.byProvider).length > 0 ? (
                  <Table
                    dataSource={Object.entries(revenueStats.byProvider).map(
                      ([provider, count]) => ({ provider, count })
                    )}
                    columns={[
                      { title: "Fournisseur", dataIndex: "provider", key: "provider" },
                      { title: "Montant", dataIndex: "count", key: "count", render: (v: any) => Number(v).toLocaleString("fr-FR") },
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="provider"
                  />
                ) : (
                  <Empty description="Aucune donnée" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Par pays">
                {revenueStats.byCountry && Object.keys(revenueStats.byCountry).length > 0 ? (
                  <Table
                    dataSource={Object.entries(revenueStats.byCountry).map(
                      ([country, count]) => ({ country, count })
                    )}
                    columns={[
                      { title: "Pays", dataIndex: "country", key: "country" },
                      { title: "Montant", dataIndex: "count", key: "count", render: (v: any) => Number(v).toLocaleString("fr-FR") },
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="country"
                  />
                ) : (
                  <Empty description="Aucune donnée" />
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Par statut">
                {revenueStats.byStatus && Object.keys(revenueStats.byStatus).length > 0 ? (
                  <Table
                    dataSource={Object.entries(revenueStats.byStatus).map(
                      ([status, count]) => ({ status, count })
                    )}
                    columns={[
                      {
                        title: "Statut",
                        dataIndex: "status",
                        key: "status",
                        render: (s: string) => <Tag color={getPaymentStatusColor(s)}>{s}</Tag>,
                      },
                      { title: "Nombre", dataIndex: "count", key: "count", render: (v: any) => Number(v).toLocaleString("fr-FR") },
                    ]}
                    pagination={false}
                    size="small"
                    rowKey="status"
                  />
                ) : (
                  <Empty description="Aucune donnée" />
                )}
              </Card>
            </Col>
          </Row>

          {(revenueStats.averageAmount != null || revenueStats.failoverRate != null) && (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {revenueStats.averageAmount != null && (
                <Col xs={24} sm={12}>
                  <Card>
                    <Statistic title="Montant moyen" value={revenueStats.averageAmount} suffix="XOF" />
                  </Card>
                </Col>
              )}
              {revenueStats.failoverRate != null && (
                <Col xs={24} sm={12}>
                  <Card>
                    <Statistic title="Taux de failover" value={revenueStats.failoverRate} suffix="%" precision={1} />
                  </Card>
                </Col>
              )}
            </Row>
          )}

          {revenueStats.topFailureReasons?.length > 0 && (
            <Card title="Principales raisons d'échec">
              <Table
                dataSource={revenueStats.topFailureReasons}
                columns={[
                  { title: "Raison", dataIndex: "reason", key: "reason" },
                  { title: "Nombre", dataIndex: "count", key: "count" },
                ]}
                pagination={false}
                size="small"
                rowKey="reason"
              />
            </Card>
          )}
        </>
      ) : (
        <Card>
          <Empty description="Aucune donnée de revenus disponible" />
        </Card>
      )}
    </Spin>
  );

  const renderBandwidthTab = () => (
    <Spin spinning={bandwidthLoading}>
      {bandwidthData.length > 0 ? (
        <Card
          title={
            <span>
              <GlobalOutlined style={{ color: "#1890ff", marginRight: 8 }} />
              Bande passante par localisation
            </span>
          }
        >
          <Table
            dataSource={bandwidthData}
            columns={[
              { title: "Localisation", dataIndex: "location", key: "location" },
              {
                title: "Bande passante moyenne",
                dataIndex: "avgBandwidth",
                key: "avgBandwidth",
                render: (val: number) => (val ? `${val.toFixed(1)} Mbps` : "--"),
              },
              {
                title: "Utilisateurs",
                dataIndex: "userCount",
                key: "userCount",
                render: (val: number) => formatNumber(val || 0),
              },
            ]}
            pagination={{ pageSize: 20, showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}` }}
            size="small"
            rowKey="location"
          />
        </Card>
      ) : (
        <Card>
          <Empty description="Aucune donnée de bande passante disponible" />
        </Card>
      )}
    </Spin>
  );

  return (
    <div>
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Title level={2} style={{ margin: 0, marginBottom: 8 }}>
            Statistiques
          </Title>
          <Text type="secondary">Analyse de performance de votre plateforme</Text>
        </div>
        <Select
          value={period}
          onChange={setPeriod}
          style={{ width: 200 }}
          options={[
            { label: "7 derniers jours", value: 7 },
            { label: "30 derniers jours", value: 30 },
            { label: "90 derniers jours", value: 90 },
          ]}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "overview", label: "Vue d'ensemble", children: renderOverviewTab() },
          { key: "content-performance", label: "Performance Contenu", children: renderContentPerformanceTab() },
          { key: "revenue", label: "Revenus", children: renderRevenueTab() },
          { key: "bandwidth", label: "Bande passante", children: renderBandwidthTab() },
        ]}
      />
    </div>
  );
};

export default StatisticsPage;
